import React from "react";
import PageBanner from "../components/PageBanner";
import Testimonials from "../components/home/Testimonials";
import CTASection from "../components/home/CTASection";
import useReveal from "../hooks/useReveal";
import Seo from "../components/Seo";
import { OWNERS, IMAGES, STATS } from "../mock";
import CountUp from "../components/CountUp";
import { Check } from "lucide-react";

const values = [
  { title: "Creativity", text: "We bring fresh, functional design thinking to every space we touch." },
  { title: "Craftsmanship", text: "Quality workmanship and attention to detail on every single project." },
  { title: "Transparency", text: "Clear communication and honest, itemized pricing from day one." },
  { title: "Consistency", text: "A dependable team and process that delivers the same high standard every time." },
];

export default function About() {
  useReveal();
  return (
    <>
      <Seo title="About Us" path="/about" description="Meet Andrew and Slava — the owners of Classic Interior Renovations, a leading Edmonton renovation company delivering quality home, office and commercial projects." />
      <PageBanner
        crumb="About"
        title="We care about the experience and the finished product"
        subtitle="A leading Edmonton construction and renovation company delivering high-quality home, office and commercial projects."
        image={IMAGES.living2}
      />

      <section className="py-24 bg-white overflow-hidden">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative reveal-left">
            <div className="shape-arch-top max-w-[480px]">
              <img src={OWNERS.image} alt="Owners Andrew and Slava" className="w-full h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-6 right-4 bg-[#2f9fe6] text-white px-7 py-6 rounded-2xl shadow-xl">
              <div className="font-display text-3xl font-bold">10+</div>
              <div className="text-white/90 text-sm">Years of craftsmanship</div>
            </div>
          </div>
          <div className="reveal-right">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Our Story</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight mb-6">
              Hi, we are {OWNERS.names}
            </h2>
            <p className="text-[#6b7683] text-lg leading-relaxed mb-6">{OWNERS.text}</p>
            <p className="text-[#6b7683] leading-relaxed mb-6">
              Each project is backed by a history of creativity, craftsmanship and attention to detail. Whether it's a kitchen remodel, bathroom renovation, basement finishing or an outdoor living space makeover, we deliver reliable, professional service that enhances the beauty and functionality of your space.
            </p>
            <div className="flex items-center gap-3 text-[#0f2740] font-medium">
              <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></span>
              If we wouldn't accept it for our own home, we won't accept it for yours.
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0f2740]">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div key={s.label} className={`reveal delay-${i + 1} text-center`}>
              <CountUp value={s.value} className="block font-display text-4xl sm:text-5xl font-bold text-white mb-3" data-testid={`about-stat-value-${i}`} />
              <div className="h-px w-14 bg-[#2f9fe6] mb-3 mx-auto" />
              <div className="text-[#7fb8de] tracking-[0.16em] text-xs font-medium uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] mb-4">What we stand for</h2>
            <p className="text-[#6b7683] text-lg">The values that guide every renovation we take on.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={v.title} className={`reveal delay-${i + 1} bg-white rounded-2xl p-8 border border-[#eef1f4] hover:shadow-xl hover:-translate-y-1 transition-all`}>
                <div className="font-display text-5xl font-bold text-[#eaf0f5] mb-3">0{i + 1}</div>
                <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-2">{v.title}</h3>
                <p className="text-[#6b7683] text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="reviews" className="scroll-mt-24">
        <Testimonials />
      </div>
      <CTASection />
    </>
  );
}
